<template>
  <div v-if="isVisible" class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <h3>Select Message Template</h3>
        <button class="close-button" @click="closeModal" aria-label="Close">
          <span>&times;</span>
        </button>
      </div>

      <div class="modal-body">
        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <i class="fa fa-spinner fa-spin"></i>
          <span>Loading templates...</span>
        </div>

        <!-- No Templates -->
        <div v-else-if="templates.length === 0" class="no-templates">
          <i class="fa fa-info-circle"></i>
          <p>No templates available for this crisis type.</p>
        </div>

        <!-- Search Bar -->
        <div v-else class="search-container">
          <div class="search-box">
            <i class="fa fa-search"></i>
            <input
              type="text"
              v-model="searchQuery"
              placeholder="Search by name or subject..."
              class="search-input"
            />
            <button
              v-if="searchQuery"
              class="clear-search"
              @click="searchQuery = ''"
              aria-label="Clear search"
            >
              <i class="fa fa-times"></i>
            </button>
          </div>
          <div class="search-results-info" v-if="searchQuery">
            Showing {{ filteredTemplates.length }} of {{ templates.length }} templates
          </div>
        </div>

        <!-- Templates Table -->
        <div v-if="!loading && templates.length > 0" class="templates-table-container">
          <table class="templates-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Subject (EN)</th>
                <th>Body (EN)</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="template in paginatedTemplates"
                :key="template.id"
                :class="{ inactive: !template.isActive }"
              >
                <td>
                  <div class="template-name">
                    {{ template.name }}
                    <span v-if="!template.isActive" class="inactive-badge">Inactive</span>
                  </div>
                </td>
                <td>
                  <div class="template-subject">{{ template.subjectEn }}</div>
                </td>
                <td>
                  <div class="template-body" v-html="template.bodyEn"></div>
                </td>
                <td>
                  <button
                    class="btn btn-sm btn-primary select-btn"
                    @click="selectTemplate(template)"
                    :disabled="!template.isActive"
                  >
                    <i class="fa fa-check"></i>
                    Select
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- No Results Message -->
          <div v-if="filteredTemplates.length === 0" class="no-results">
            <i class="fa fa-search"></i>
            <p>No templates match your search criteria.</p>
            <button class="btn btn-sm btn-secondary" @click="searchQuery = ''">
              Clear Search
            </button>
          </div>

          <!-- Pagination Controls -->
          <div v-if="filteredTemplates.length > 0" class="pagination-container">
            <div class="pagination-info">
              Showing {{ startIndex + 1 }}-{{ endIndex }} of {{ filteredTemplates.length }} templates
            </div>
            
            <div class="pagination-controls">
              <button
                class="pagination-btn"
                @click="goToFirstPage"
                :disabled="currentPage === 1"
                aria-label="First page"
              >
                <i class="fa fa-angle-double-left"></i>
              </button>
              
              <button
                class="pagination-btn"
                @click="previousPage"
                :disabled="currentPage === 1"
                aria-label="Previous page"
              >
                <i class="fa fa-angle-left"></i>
              </button>
              
              <div class="page-numbers">
                <button
                  v-for="page in visiblePages"
                  :key="page"
                  class="page-number"
                  :class="{ active: page === currentPage }"
                  @click="goToPage(page)"
                >
                  {{ page }}
                </button>
              </div>
              
              <button
                class="pagination-btn"
                @click="nextPage"
                :disabled="currentPage === totalPages"
                aria-label="Next page"
              >
                <i class="fa fa-angle-right"></i>
              </button>
              
              <button
                class="pagination-btn"
                @click="goToLastPage"
                :disabled="currentPage === totalPages"
                aria-label="Last page"
              >
                <i class="fa fa-angle-double-right"></i>
              </button>
            </div>

            <div class="items-per-page">
              <label for="itemsPerPage">Items per page:</label>
              <select id="itemsPerPage" v-model.number="itemsPerPage">
                <option :value="5">5</option>
                <option :value="10">10</option>
                <option :value="25">25</option>
                <option :value="50">50</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeModal">
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TemplateSelectionModal",
  props: {
    isVisible: { type: Boolean, required: true },
    templates: { type: Array, required: true },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      searchQuery: "",
      currentPage: 1,
      itemsPerPage: 10,
    };
  },
  computed: {
    filteredTemplates() {
      if (!this.searchQuery.trim()) {
        return this.templates;
      }

      const query = this.searchQuery.toLowerCase().trim();
      return this.templates.filter((template) => {
        const name = (template.name || "").toLowerCase();
        const subject = (template.subjectEn || "").toLowerCase();
        return name.includes(query) || subject.includes(query);
      });
    },
    totalPages() {
      return Math.ceil(this.filteredTemplates.length / this.itemsPerPage);
    },
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    endIndex() {
      return Math.min(this.startIndex + this.itemsPerPage, this.filteredTemplates.length);
    },
    paginatedTemplates() {
      return this.filteredTemplates.slice(this.startIndex, this.endIndex);
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      // Adjust start if we're near the end
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
  },
  watch: {
    isVisible(newVal) {
      // Clear search and reset pagination when modal is closed
      if (!newVal) {
        this.searchQuery = "";
        this.currentPage = 1;
      }
    },
    searchQuery() {
      // Reset to first page when search query changes
      this.currentPage = 1;
    },
    itemsPerPage() {
      // Reset to first page when items per page changes
      this.currentPage = 1;
    },
  },
  methods: {
    handleOverlayClick(event) {
      if (event.target === event.currentTarget) {
        this.closeModal();
      }
    },
    closeModal() {
      this.$emit("close");
    },
    selectTemplate(template) {
      this.$emit("select-template", template);
      this.closeModal();
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    goToFirstPage() {
      this.currentPage = 1;
    },
    goToLastPage() {
      this.currentPage = this.totalPages;
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20000;
  padding: 20px;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  max-width: 1000px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;
}

@keyframes slideIn {
  from {
    transform: scale(0.9) translateY(-20px);
    opacity: 0;
  }
  to {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 2px solid #007bff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.modal-header h3 {
  margin: 0;
  color: white;
  font-size: 20px;
  font-weight: 600;
}

.close-button {
  background: none;
  border: none;
  font-size: 28px;
  color: white;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.modal-body {
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
  background: #fafafa;
}

.search-container {
  margin-bottom: 20px;
  background: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.search-box i.fa-search {
  position: absolute;
  left: 12px;
  color: #6c757d;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 10px 40px 10px 40px;
  border: 2px solid #e9ecef;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.clear-search {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.clear-search:hover {
  background: #f8f9fa;
  color: #495057;
}

.search-results-info {
  margin-top: 8px;
  font-size: 13px;
  color: #6c757d;
  font-style: italic;
}

.loading-state,
.no-templates {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #6c757d;
  gap: 12px;
}

.loading-state i,
.no-templates i {
  font-size: 48px;
  color: #007bff;
}

.no-templates p {
  margin: 0;
  font-size: 16px;
}

.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #6c757d;
  gap: 12px;
  background: white;
  border-radius: 8px;
  margin-top: 20px;
}

.no-results i {
  font-size: 48px;
  color: #6c757d;
}

.no-results p {
  margin: 0;
  font-size: 16px;
}

.templates-table-container {
  overflow-x: auto;
}

.pagination-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  flex-wrap: wrap;
  gap: 12px;
}

.pagination-info {
  font-size: 14px;
  color: #495057;
  font-weight: 500;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.pagination-btn,
.page-number {
  background: white;
  border: 1px solid #dee2e6;
  color: #495057;
  cursor: pointer;
  padding: 6px 10px;
  font-size: 14px;
  border-radius: 4px;
  transition: all 0.2s ease;
  min-width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-btn:hover:not(:disabled),
.page-number:hover:not(.active) {
  background: #f8f9fa;
  border-color: #007bff;
  color: #007bff;
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 4px;
}

.page-number.active {
  background: #007bff;
  border-color: #007bff;
  color: white;
  font-weight: 600;
}

.items-per-page {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #495057;
}

.items-per-page label {
  font-weight: 500;
  margin: 0;
}

.items-per-page select {
  padding: 6px 10px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: white;
  color: #495057;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}

.items-per-page select:hover {
  border-color: #007bff;
}

.items-per-page select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.templates-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.templates-table thead {
  background: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 1;
}

.templates-table th {
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 2px solid #dee2e6;
}

.templates-table td {
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
  vertical-align: top;
}

.templates-table tr.inactive {
  opacity: 0.6;
  background-color: #f8f9fa;
}

.templates-table tbody tr:hover:not(.inactive) {
  background-color: #f8f9fa;
}

.template-name {
  font-weight: 500;
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 8px;
}

.inactive-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #ffc107;
  color: #856404;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.template-subject {
  font-weight: 500;
  color: #495057;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.template-body {
  max-width: 350px;
  max-height: 80px;
  overflow: hidden;
  color: #6c757d;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
}

.template-body >>> p {
  margin: 0 0 8px 0;
}

.template-body >>> strong {
  color: #2c3e50;
}

.select-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  white-space: nowrap;
}

.select-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
  gap: 12px;
  flex-shrink: 0;
  background: white;
}

.btn {
  padding: 10px 24px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

@media (max-width: 768px) {
  .modal-container {
    max-width: 100%;
    width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }

  .templates-table {
    font-size: 12px;
  }

  .templates-table th,
  .templates-table td {
    padding: 8px;
  }

  .template-subject,
  .template-body {
    max-width: 150px;
  }

  .pagination-container {
    flex-direction: column;
    align-items: stretch;
  }

  .pagination-info,
  .items-per-page {
    justify-content: center;
  }

  .pagination-controls {
    justify-content: center;
    flex-wrap: wrap;
  }
}
</style>
